import { useCallback, useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";

export default function useBlackjackAdminTable(tableId) {
  const [state, setState] = useState(null);
  const [transport, setTransport] = useState("polling");
  const [lastUpdateAt, setLastUpdateAt] = useState(0);
  // First-load error: set only when the very first state fetch fails (state still
  // null). Lets the operator console distinguish "couldn't load the table" from the
  // normal "connecting…" so it doesn't spin forever with no explanation. Cleared the
  // moment any successful fetch applies state.
  const [loadError, setLoadError] = useState("");
  const alive = useRef(true);
  const revisionRef = useRef(-1);

  const apply = useCallback((next) => {
    if (!next) return;
    const revision = Number(next.revision ?? 0);
    if (revision <= revisionRef.current) return;
    revisionRef.current = revision;
    setState(next);
    setLastUpdateAt(Date.now());
  }, []);

  const poll = useCallback(async () => {
    if (!tableId) return;
    const { data } = await api.get(`/admin/bj/state?table=${tableId}`);
    if (alive.current) {
      // Liveness: any successful fetch proves the connection, even if the table
      // revision is unchanged (idle). Otherwise an idle table falsely goes stale
      // and pauses the operator's card/money controls.
      setLastUpdateAt(Date.now());
      setLoadError(""); // a successful fetch clears any first-load error
      apply(data);
    }
  }, [apply, tableId]);

  useEffect(() => {
    alive.current = true;
    revisionRef.current = -1;
    setState(null);
    setLastUpdateAt(0);
    setLoadError("");
    if (!tableId) return () => { alive.current = false; };

    let ws;
    let retry;
    let heartbeat;
    let stopped = false;

    // First state load — if it fails while we still have no state, surface a
    // first-load error so the console can show a real message + retry instead of
    // an endless "connecting…". The 4s fallback poll keeps trying and will clear it.
    poll().catch((e) => {
      if (alive.current) {
        setLoadError(e?.response?.data?.detail || "Could not load the table. Retrying…");
      }
    });

    const connect = async () => {
      try {
        const { data } = await api.post(`/casino/bj/socket-ticket?table=${tableId}`);
        const wsBase = API.replace(/^http:/, "ws:").replace(/^https:/, "wss:");
        ws = new WebSocket(`${wsBase}/ws/blackjack/${tableId}?ticket=${encodeURIComponent(data.ticket)}`);
        ws.onopen = () => {
          if (stopped) return;
          setTransport("realtime");
          clearInterval(heartbeat);
          heartbeat = setInterval(() => {
            if (ws?.readyState === WebSocket.OPEN) ws.send("ping");
          }, 20000);
        };
        ws.onmessage = (event) => {
          try {
            const packet = JSON.parse(event.data);
            if (packet?.type === "state") {
              setLastUpdateAt(Date.now());
              apply(packet.state);
            }
          } catch {}
        };
        ws.onerror = () => !stopped && setTransport("polling");
        ws.onclose = () => {
          clearInterval(heartbeat);
          if (stopped) return;
          setTransport("polling");
          retry = setTimeout(connect, 2000);
        };
      } catch {
        if (stopped) return;
        setTransport("polling");
        retry = setTimeout(connect, 3000);
      }
    };

    connect();
    const fallback = setInterval(() => poll().catch(() => {}), 4000);
    return () => {
      alive.current = false;
      stopped = true;
      clearInterval(fallback);
      clearInterval(heartbeat);
      clearTimeout(retry);
      try { ws?.close(); } catch {}
    };
  }, [apply, poll, tableId]);

  return { state, setState: apply, poll, transport, lastUpdateAt, loadError };
}
